import {
  getIndicatorChartData,
  clearIndicatorChartData,
  setIndicatorChartFilters,
} from '../../actions/PageActions.js'
import {
  considerationToDimension,
  dimensionToColor,
  getSelectedQuarterString,
  parseSelectedQuarterValue,
  getConsiderationByDimensionKey,
} from '../../constants/Constants'
import { CitiIQCard } from '../CitiIQCard/CitiIQCard.js'
import { LoadingScreen } from '../LoadingScreen/LoadingScreen.js'
import HorizontalBar from './HorizontalBar.js'
import './IndicatorChart.scss'
import {
  Button,
  FormControlLabel,
  Input,
  MenuItem,
  Select,
  Switch,
  Typography,
} from '@material-ui/core'
import { Map } from 'immutable'
import React, { useState, useEffect } from 'react'
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl'
import { connect } from 'react-redux'
import { bindActionCreators, Dispatch } from 'redux'

interface AffectedConsideration {
  readonly i: number
  readonly c: number
  readonly w: number
}

interface ChartDataItem {
  readonly id: number
  readonly name: string
  readonly description: string
  readonly value: number
  readonly year: number
  readonly quarter: number
  readonly quarterString: string
  readonly isSelectedQuarter: boolean
  readonly affectedConsiderations: AffectedConsideration[]
}

interface IndicatorWeight {
  readonly i: number
  readonly c: number
  readonly w: number
}

interface Indicator {
  readonly id: number
  readonly value: number
}

interface IndicatorChartDataItem {
  readonly indicatorWeights: IndicatorWeight[] | null
  readonly indicators: Indicator[] | null
  readonly year: number
  readonly quarter: number
}

interface Quarter {
  readonly year: number
  readonly quarter: number
}

interface ConsiderationInfo {
  readonly icon: string
  readonly label: string
  readonly dimension: number
}

interface DimensionColor {
  readonly main: string
  readonly target: string
  readonly secondary: string
  readonly fill: string
  readonly considerations?: string[]
}

type ConsiderationToDimensionMap = Record<number, ConsiderationInfo | undefined>
type DimensionToColorMap = Record<number, DimensionColor | undefined>

interface ImmutableStore {
  getIn: (path: string[]) => Map<string, unknown>
}

interface Actions {
  readonly getIndicatorChartData: (
    cityId: number,
    year: number,
    quarter: number
  ) => void
  readonly clearIndicatorChartData: () => void
  readonly setIndicatorChartFilters: (filters: {
    selectedDimension: number
    selectedConsideration: number
    searchKey: string
    isShowingPrevQuarters: boolean
  }) => void
}

interface IndicatorChartProps {
  readonly pageStore: ImmutableStore
  readonly userStore: ImmutableStore
  readonly intl: IntlShape
  readonly actions: Actions
}

type MapByCid = Partial<Record<number, AffectedConsideration[]>>
type MapByIidWithPrevQuarters = Partial<
  Record<string, Partial<Record<number, AffectedConsideration[]>>>
>

const typedConsiderationToDimension =
  considerationToDimension as ConsiderationToDimensionMap
const typedDimensionToColor = dimensionToColor as DimensionToColorMap

const IndicatorChart: React.FC<IndicatorChartProps> = ({
  pageStore,
  userStore,
  intl,
  actions,
}) => {
  const [searchKey, setSearchKey] = useState<string>('')
  const [selectedDimension, setSelectedDimension] = useState<number>(0)
  const [selectedConsideration, setSelectedConsideration] = useState<number>(0)
  const [filteredChartData, setFilteredChartData] = useState<ChartDataItem[]>(
    []
  )
  const [mapByCid, setMapByCid] = useState<MapByCid>({})
  const [chartHeight, setChartHeight] = useState<number>(0)
  const [prevQuartersChartData, setPrevQuartersChartData] = useState<
    ChartDataItem[]
  >([])
  const [mapByIidWithPrevQuarters, setMapByIidWithPrevQuarters] =
    useState<MapByIidWithPrevQuarters>({})
  const [isShowingPrevQuarters, setIsShowingPrevQuarters] =
    useState<boolean>(false)

  const isFilterApplied =
    (searchKey !== '' && searchKey.length >= 3) ||
    selectedDimension !== 0 ||
    selectedConsideration !== 0

  // Sync filter state to Redux for AI context
  useEffect(() => {
    actions.setIndicatorChartFilters({
      selectedDimension,
      selectedConsideration,
      searchKey,
      isShowingPrevQuarters,
    })
  }, [
    searchKey,
    selectedDimension,
    selectedConsideration,
    isShowingPrevQuarters,
  ])

  const getStoreValue = <T,>(
    store: ImmutableStore,
    path: string[]
  ): T | undefined => {
    const value = store.getIn(path) as unknown
    if (
      value !== null &&
      value !== undefined &&
      typeof value === 'object' &&
      'toJS' in value &&
      typeof (value as { toJS: () => unknown }).toJS === 'function'
    ) {
      return (value as { toJS: () => T }).toJS()
    }
    return value as T | undefined
  }

  // Get raw Immutable values for dependency tracking
  const rawIndicatorChartData = pageStore.getIn(['indicatorChartData'])
  const rawSelectedQuarter = userStore.getIn(['selectedQuarter'])
  const rawSelectedCityId = userStore.getIn(['selectedCityId'])

  const allQuarters: Quarter[] =
    getStoreValue<Quarter[]>(pageStore, ['allQuarters']) ?? []

  const indicatorChartData: IndicatorChartDataItem[] =
    getStoreValue<IndicatorChartDataItem[]>(pageStore, [
      'indicatorChartData',
    ]) ?? []

  const hasOnlyOneQuarterAvailable = indicatorChartData.length === 1

  const isLoadingSingleCityInfo =
    getStoreValue<boolean>(pageStore, ['isLoadingSingleCityInfo']) ?? false

  const selectedQuarterValue =
    getStoreValue<string>(userStore, ['selectedQuarter']) ?? ''

  const selectedCityIdValue =
    getStoreValue<number>(userStore, ['selectedCityId']) ?? 0

  useEffect(() => {
    actions.clearIndicatorChartData()

    const parsed = parseSelectedQuarterValue(selectedQuarterValue)
    if (!parsed) return
    const { year, quarter } = parsed

    /*
     * We want to show 4 quarters (4 snapshots)
     * Starting from the current quarter
     */
    const total = 4
    let foundSnapshot = false
    let snapshotToAdd = total

    const prevQuartersList = allQuarters.filter((q) => {
      if (year === q.year && quarter === q.quarter) {
        foundSnapshot = true
      }
      if (foundSnapshot && snapshotToAdd > 0) {
        snapshotToAdd -= 1
        return true
      }
      return false
    })

    prevQuartersList.forEach((q) => {
      actions.getIndicatorChartData(selectedCityIdValue, q.year, q.quarter)
    })
  }, [rawSelectedQuarter, rawSelectedCityId])

  useEffect(() => {
    if (indicatorChartData.length === 0) return

    const mapCid: MapByCid = {}
    const mapIid: MapByIidWithPrevQuarters = {}

    indicatorChartData.forEach(({ indicatorWeights, year, quarter }, idx) => {
      const q = getSelectedQuarterString({ year, quarter })
      const weights = indicatorWeights ?? []

      weights.forEach((w) => {
        const item: AffectedConsideration = {
          i: w.i,
          c: w.c,
          w: w.w,
        }

        if (idx === 0) {
          const cidKey = item.c
          if (!mapCid[cidKey]) {
            mapCid[cidKey] = []
          }
          mapCid[cidKey].push(item)
        }

        const iidKey = item.i
        if (!mapIid[q]) {
          mapIid[q] = {}
        }
        // Non-null assertion safe here because we just set it above
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const qMap = mapIid[q]!
        if (!qMap[iidKey]) {
          qMap[iidKey] = []
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        qMap[iidKey]!.push(item)
      })
    })

    setMapByCid(mapCid)
    setMapByIidWithPrevQuarters(mapIid)
  }, [rawIndicatorChartData])

  useEffect(() => {
    if (indicatorChartData.length === 0) return
    if (Object.keys(mapByIidWithPrevQuarters).length === 0) return

    interface IndicatorWithQuarter {
      id: number
      value: number
      year: number
      quarter: number
      quarterString: string
      isSelectedQuarter: boolean
    }

    let allIndicators: IndicatorWithQuarter[] = []

    indicatorChartData.forEach(({ indicators, year, quarter }) => {
      const indicatorList = indicators ?? []
      const mapped = indicatorList.map((w) => ({
        id: w.id,
        value: w.value,
        year,
        quarter,
        quarterString: getSelectedQuarterString({ year, quarter }),
        isSelectedQuarter:
          getSelectedQuarterString({ year, quarter }) === selectedQuarterValue,
      }))
      allIndicators = [...allIndicators, ...mapped]
    })

    const data: ChartDataItem[] = allIndicators
      .map((i) => ({
        ...i,
        name: `${intl.formatMessage({ id: `indicators.name.${i.id}` })}`,
        description: intl.formatMessage({
          id: `indicators.description.${i.id}`,
        }),
        value: Number(i.value) * 100,
        affectedConsiderations:
          mapByIidWithPrevQuarters[i.quarterString]?.[i.id] ?? [],
      }))
      .sort((a, b) => a.name.localeCompare(b.name))

    setPrevQuartersChartData(data)
  }, [
    intl,
    mapByIidWithPrevQuarters,
    rawIndicatorChartData,
    rawSelectedQuarter,
  ])

  useEffect(() => {
    if (!isFilterApplied) {
      setFilteredChartData([])
      return
    }

    let filtered = [...prevQuartersChartData]

    if (!isShowingPrevQuarters) {
      filtered = filtered.filter((d) => d.isSelectedQuarter)
    }

    if (searchKey !== '' && searchKey.length >= 3) {
      filtered = filtered.filter((i) =>
        `${i.name} ${i.description}`
          .toLowerCase()
          .includes(searchKey.toLowerCase())
      )
    }

    if (selectedConsideration !== 0) {
      filtered = filtered.filter((i) =>
        i.affectedConsiderations.some(
          (c) => String(c.c) === String(selectedConsideration)
        )
      )
    }

    if (selectedDimension !== 0) {
      filtered = filtered.filter((i) =>
        i.affectedConsiderations.some((c) => {
          const consideration = typedConsiderationToDimension[c.c]
          return (
            consideration &&
            String(consideration.dimension) === String(selectedDimension)
          )
        })
      )
    }

    setFilteredChartData(filtered)
  }, [
    searchKey,
    selectedDimension,
    selectedConsideration,
    prevQuartersChartData,
    isFilterApplied,
    isShowingPrevQuarters,
  ])

  useEffect(() => {
    let data = isFilterApplied ? filteredChartData : prevQuartersChartData

    if (!isShowingPrevQuarters) {
      data = data.filter((d) => d.isSelectedQuarter)
    }

    const multiplier =
      isShowingPrevQuarters && !hasOnlyOneQuarterAvailable ? 15 : 30
    const h = data.length * multiplier

    setChartHeight(h < 250 ? 250 : h)
  }, [
    filteredChartData,
    prevQuartersChartData,
    isFilterApplied,
    isShowingPrevQuarters,
    hasOnlyOneQuarterAvailable,
  ])

  const mayFilterDimensionIds = (items: number[]): number[] => {
    if (selectedConsideration === 0) {
      return items
    }
    const consideration = typedConsiderationToDimension[selectedConsideration]
    return consideration ? [consideration.dimension] : items
  }

  const mayFilterConsiderationIds = (items: string[]): string[] => {
    if (selectedDimension !== 0) {
      return getConsiderationByDimensionKey(selectedDimension, true) as string[]
    }
    if (selectedConsideration !== 0) {
      const consideration = typedConsiderationToDimension[selectedConsideration]
      if (consideration) {
        return getConsiderationByDimensionKey(
          consideration.dimension,
          true
        ) as string[]
      }
    }
    return items
  }

  const getConsiderationDimensionColor = (
    considerationId: number | string
  ): string => {
    const numId =
      typeof considerationId === 'string'
        ? parseInt(considerationId, 10)
        : considerationId
    const consideration = typedConsiderationToDimension[numId]
    if (consideration !== undefined) {
      const color = typedDimensionToColor[consideration.dimension]
      if (color !== undefined) {
        return color.main
      }
    }
    return '#9EC73E'
  }

  const getDimensionColor = (dimensionId: number): string => {
    const color = typedDimensionToColor[dimensionId]
    if (color !== undefined) {
      return color.main
    }
    return '#9EC73E'
  }

  const getSelectedConsiderationDimension = (): number => {
    const consideration = typedConsiderationToDimension[selectedConsideration]
    if (consideration !== undefined) {
      return consideration.dimension
    }
    return 0
  }

  const getChartData = (): ChartDataItem[] => {
    if (isFilterApplied) {
      return filteredChartData
    }
    if (isShowingPrevQuarters) {
      return prevQuartersChartData
    }
    return prevQuartersChartData.filter((d) => d.isSelectedQuarter)
  }

  const chartData = getChartData()

  const chart = (
    <div style={{ height: '100%', width: '100%' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          margin: '-2rem 1rem 2rem',
        }}
      >
        <div />
        <Button
          style={{ backgroundColor: '#9EC73E' }}
          onClick={() => {
            setSelectedConsideration(0)
            setSelectedDimension(0)
            setSearchKey('')
          }}
        >
          <FormattedMessage id="indicator.chart.clearFilters" />
        </Button>
      </div>
      <div
        className="chartHelperWrapper"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          margin: '1rem',
        }}
      >
        <div className="chartHelper">
          <div>
            <Typography variant="subtitle1">
              <FormattedMessage id="indicator.chart.searchBy" />
            </Typography>
            <Input
              fullWidth
              value={searchKey}
              onChange={(e) => setSearchKey(e.target.value)}
              placeholder={intl.formatMessage({
                id: 'indicator.chart.searchPlaceholder',
              })}
            />
          </div>
        </div>
        <div className="chartHelper">
          <div className="select">
            <Typography variant="subtitle1">
              {' '}
              <FormattedMessage id="indicator.chart.dimension" />{' '}
            </Typography>
            {selectedConsideration !== 0 ? (
              <div>
                <span
                  style={{
                    display: 'inline-block',
                    width: '10px',
                    height: '10px',
                    backgroundColor: getConsiderationDimensionColor(
                      selectedConsideration
                    ),
                    borderRadius: '50%',
                    marginRight: '5px',
                  }}
                />
                <FormattedMessage
                  id={`dimensions.${String(
                    getSelectedConsiderationDimension()
                  )}`}
                  description="option-text"
                />
              </div>
            ) : (
              <Select
                value={selectedDimension}
                onChange={(e) => {
                  setSelectedDimension(Number(e.target.value))
                  setSelectedConsideration(0)
                }}
                inputProps={{
                  name: 'selectedDimension',
                }}
                disableUnderline
                autoWidth
                classes={{ select: 'dropdown-select' }}
              >
                <MenuItem key={0} value={0}>
                  --
                </MenuItem>
                {mayFilterDimensionIds([1, 2, 3, 4, 5]).map((d) => (
                  <MenuItem key={d} value={d}>
                    <span
                      style={{
                        display: 'inline-block',
                        width: '10px',
                        height: '10px',
                        backgroundColor: getDimensionColor(d),
                        borderRadius: '50%',
                        marginRight: '5px',
                      }}
                    />
                    <FormattedMessage
                      id={`dimensions.${d}`}
                      description="option-text"
                    />
                  </MenuItem>
                ))}
              </Select>
            )}
          </div>
          <div className="select">
            <Typography variant="subtitle1">
              <FormattedMessage id="indicator.chart.consideration" />{' '}
            </Typography>
            <Select
              value={selectedConsideration}
              onChange={(e) => {
                setSelectedConsideration(Number(e.target.value))
                setSelectedDimension(0)
              }}
              inputProps={{
                name: 'selectedConsideration',
              }}
              disableUnderline
              autoWidth
              classes={{ select: 'dropdown-select' }}
            >
              <MenuItem key={0} value={0}>
                --
              </MenuItem>
              {mayFilterConsiderationIds(Object.keys(mapByCid)).map((c) => (
                <MenuItem key={c} value={c}>
                  <span
                    style={{
                      display: 'inline-block',
                      width: '10px',
                      height: '10px',
                      backgroundColor: getConsiderationDimensionColor(c),
                      borderRadius: '50%',
                      marginRight: '5px',
                    }}
                  />
                  <FormattedMessage
                    id={`considerations.${c}`}
                    description="option-text"
                  />
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>
      </div>
      <div style={{ margin: '0 1rem' }}>
        <FormControlLabel
          control={
            hasOnlyOneQuarterAvailable ? (
              <div style={{ width: '1rem' }} />
            ) : (
              <Switch
                checked={isShowingPrevQuarters}
                onChange={(e) => {
                  setIsShowingPrevQuarters(e.target.checked)
                }}
              />
            )
          }
          label={intl.formatMessage({
            id: hasOnlyOneQuarterAvailable
              ? 'indicator.chart.noPreviousQuarters'
              : 'indicator.chart.showPreviousQuarters',
          })}
        />
      </div>
      <div className="horizontalChart">
        {isFilterApplied && filteredChartData.length === 0 ? (
          <Typography
            style={{
              textAlign: 'center',
              margin: '2rem 0',
            }}
          >
            <FormattedMessage id="indicator.chart.notFound" />
          </Typography>
        ) : (
          <HorizontalBar
            isShowingPrevQuarters={isShowingPrevQuarters}
            quartersCount={indicatorChartData.length}
            searchKey={searchKey.length >= 3 ? searchKey : ''}
            data={chartData}
            selectedConsideration={selectedConsideration}
            chartHeight={chartHeight}
          />
        )}
      </div>
    </div>
  )

  return (
    // @ts-expect-error - CitiIQCard props mismatch with JS component
    <CitiIQCard
      height={chartHeight + 400}
      className="dimension-comparison-wrapper"
    >
      <h3 className="chartHeader">
        <FormattedMessage
          id="indicator.comparison.navigation"
          description="header"
        />
      </h3>
      {isLoadingSingleCityInfo ? (
        <LoadingScreen />
      ) : (
        <div className="comparisonChartDiv">{chart}</div>
      )}
    </CitiIQCard>
  )
}

interface RootState {
  readonly PageStore: ImmutableStore
  readonly UserStore: ImmutableStore
}

function mapStateToProps(state: RootState) {
  return {
    pageStore: state.PageStore,
    userStore: state.UserStore,
  }
}

function mapDispatchToProps(dispatch: Dispatch): { actions: Actions } {
  return {
    actions: bindActionCreators(
      {
        getIndicatorChartData,
        clearIndicatorChartData,
        setIndicatorChartFilters,
      },
      dispatch
    ) as unknown as Actions,
  }
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-call -- react-redux connect returns any
const ConnectedIndicatorChart = connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(IndicatorChart)) as React.ComponentType

export default ConnectedIndicatorChart
